import { setFilter } from "./store.js";

/**
 * Build a category facet UI inside a fieldset and wire it to the store.
 * - Aggregates categories from items
 * - Preselects from URL ?cat=… params (multiple allowed)
 * - Emits `setFilter({ categories: [...] })` on change
 *
 * @param {import("./store.js").Item[]} items
 * @param {HTMLFieldSetElement|null} fieldsetEl
 * @returns {Map<string, number>} A map of category → count
 */
export function buildFacetUI(items, fieldsetEl) {
  if (!fieldsetEl) return new Map();

  // Aggregate categories with counts
  const cats = new Map();
  for (const it of items) {
    for (const c of it.categories || []) {
      cats.set(c, (cats.get(c) || 0) + 1);
    }
  }

  // Preselect from URL (?cat=Foo&cat=Bar)
  const params = new URLSearchParams(location.search);
  const selected = new Set(params.getAll("cat"));

  // Render facet
  fieldsetEl.innerHTML = "";
  const legend = document.createElement("legend");
  legend.textContent = "Categories";
  fieldsetEl.appendChild(legend);

  const frag = document.createDocumentFragment();

  // Sort by label (locale-aware) for stable UI
  const entries = [...cats.entries()].sort(([a], [b]) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );

  for (const [name, count] of entries) {
    const safeId = `cat-${name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")}`;

    const label = document.createElement("label");
    label.setAttribute("for", safeId);
    label.style.display = "flex";
    label.style.gap = "0.5rem";
    label.style.alignItems = "center";

    const input = document.createElement("input");
    input.type = "checkbox";
    input.id = safeId;
    input.value = name;
    input.checked = selected.has(name);

    const text = document.createElement("span");
    text.innerHTML = `${escapeHtml(name)} <small>(${count})</small>`;

    label.appendChild(input);
    label.appendChild(text);
    frag.appendChild(label);
  }

  fieldsetEl.appendChild(frag);

  // Wire change handler → store
  fieldsetEl.addEventListener("change", () => {
    const picked = Array.from(
      fieldsetEl.querySelectorAll('input[type="checkbox"]:checked')
    ).map((i) => /** @type {HTMLInputElement} */ (i).value);
    setFilter({ categories: picked });
  });

  return cats;
}

/**
 * Minimal HTML escaper for facet labels rendered via innerHTML.
 * @param {string} s
 * @returns {string}
 */
function escapeHtml(s) {
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
